import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatApiError = (detail) => {
  if (!detail) return "Unable to sign in right now.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || JSON.stringify(item)).join(" ");
  return String(detail);
};

export default function LeadTrackerLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitLogin = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true },
      );
      toast.success("Lead tracker unlocked.");
      navigate("/leads-dashboard");
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-28 lg:pt-20" data-testid="lead-tracker-login-page">
      <div className="mx-auto max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="section-kicker">Private Access</p>
          <h1 className="section-heading mt-5 max-w-[12ch] text-white">Owner login for the private lead tracker.</h1>
          <p className="section-copy mt-6 text-white/[0.64]">
            This route is reserved for the Equinox owner so lead data stays private while the public website remains fully open.
          </p>
        </motion.div>

        <form onSubmit={submitLogin} className="editorial-shell glitter-card glass-panel mt-12 rounded-[2.2rem] p-6 lg:p-8" data-testid="lead-tracker-login-form">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.1] bg-white/[0.06] text-blue-100">
            <LockKeyhole className="h-5 w-5" />
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <label className="block text-sm text-white/[0.75]">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                data-testid="lead-tracker-login-email-input"
              />
            </label>

            <label className="block text-sm text-white/[0.75]">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                data-testid="lead-tracker-login-password-input"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="glass-button glass-panel mt-6 rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-all duration-300 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="lead-tracker-login-submit-button"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              {submitting ? "Unlocking..." : "Unlock Lead Tracker"}
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </form>
      </div>
    </section>
  );
}